/**
 * Client-side storage for the OAuth flow secret issued by `/api/auth/sso/link`.
 *
 * The secret is what proves a returning redirect belongs to the client that
 * started the flow (see `api/util/oauth-binding.ts`). It has to survive a
 * full-page navigation out to the identity provider and back, so it lives in
 * `localStorage` rather than component state or `sessionStorage` — the latter is
 * per-tab, and some OAuth flows are opened in a new tab.
 *
 * Storing it here is not a downgrade: the session token already lives in
 * `localStorage`, so anything able to read this can read that instead.
 *
 * One flow at a time. A second `/link` before the first returns overwrites the
 * secret and the abandoned flow fails closed at the callback, which is the
 * correct outcome for a flow the user walked away from.
 */

const STORAGE_KEY = "ssoFlowSecret";

export const storeSsoFlowSecret = (secret: string): void => {
	try {
		localStorage.setItem(STORAGE_KEY, secret);
	} catch {
		// Storage disabled (private mode, quota). The callback will reject the flow
		// with a normal sign-in error, which is the honest outcome — we cannot prove
		// the redirect belongs to this client without it.
	}
};

export const readSsoFlowSecret = (): string | null => {
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
};

export const clearSsoFlowSecret = (): void => {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Nothing to do — a leftover secret is inert once its state has expired.
	}
};
